import {
  CurveConfig,
  DEFAULT_CURVE_CONFIG,
  createLaunchValue,
  toNano,
} from '@gong/curve-math';
import { useMemo, useState } from 'react';

import { HAS_FACTORY } from '../config';
import { Copy } from '../i18n';
import { formatJettons, formatTon, parseTon } from '../lib/format';
import { quoteLaunch, quoteOrError } from '../quotes/quote';
import { launchPassportLines } from '../quotes/passport';
import { PhaseBadge } from '../ui/PhaseBadge';
import { RiskNote } from '../ui/RiskNote';
import { SeedDevBuyLines } from '../ui/SeedDevBuyLines';

export interface LaunchDraft {
  name: string;
  ticker: string;
  imageBase64: string;
  imageMime: string;
  seedTon: string;
  devBuyTon: string;
}

export interface LaunchSubmit {
  name: string;
  ticker: string;
  imageBase64: string;
  imageMime: string;
  seed: bigint;
  devBuy: bigint;
}

const emptyDraft: LaunchDraft = {
  name: '',
  ticker: '',
  imageBase64: '',
  imageMime: '',
  seedTon: '0',
  devBuyTon: '0',
};

export function LaunchScreen({
  copy,
  config = DEFAULT_CURVE_CONFIG,
  ready = true,
  launchFee,
  paused,
  busy,
  busyLabel,
  error,
  channelSelected = false,
  channelImageUrl = null,
  lockedName,
  lockedTicker,
  onSubmit,
}: {
  copy: Copy;
  config?: CurveConfig;
  ready?: boolean;
  launchFee: bigint;
  paused: boolean;
  busy: boolean;
  busyLabel?: string | null;
  error: string | null;
  channelSelected?: boolean;
  channelImageUrl?: string | null;
  lockedName?: string;
  lockedTicker?: string;
  onSubmit: (draft: LaunchSubmit) => void;
}) {
  const [draft, setDraft] = useState<LaunchDraft>(emptyDraft);
  const [localError, setLocalError] = useState<string | null>(null);
  const seed = safeParse(draft.seedTon);
  const devBuy = safeParse(draft.devBuyTon);
  const preview = useMemo(
    () => quoteOrError(() => quoteLaunch(seed, devBuy, { config })),
    [seed, devBuy, config],
  );

  const total = createLaunchValue({ launchFee, seedTon: seed, devBuyTon: devBuy });
  const ticker = ((lockedTicker ?? draft.ticker) || 'TOKEN').toUpperCase();
  const shownError = localError ?? error;
  const imagePreview = draft.imageBase64
    ? `data:${draft.imageMime};base64,${draft.imageBase64}` : channelImageUrl;

  function submit(): void {
    setLocalError(null);
    if (!preview.ok) {
      setLocalError(preview.error.message);
      return;
    }
    if (channelSelected && !imagePreview) {
      setLocalError(copy.metadataRequired);
      return;
    }
    onSubmit({
      name: (lockedName ?? draft.name).trim() || ticker,
      ticker,
      imageBase64: draft.imageBase64,
      imageMime: draft.imageMime,
      seed,
      devBuy,
    });
  }

  return (
    <div className="scroll">
      {!HAS_FACTORY ? <div className="banner warn">{copy.noFactory}</div> : null}
      {!ready && !error ? <div className="banner">Проверяем параметры тестовой фабрики…</div> : null}
      {paused ? <div className="banner warn">{copy.factoryPaused}</div> : null}

      <div className="section-label">{copy.launchToken}</div>
      <div className="group">
        <Field label={copy.name} value={lockedName ?? draft.name} disabled={channelSelected} onChange={(name) => setDraft((d) => ({ ...d, name }))} />
        <Field
          label={copy.ticker}
          value={lockedTicker ?? draft.ticker}
          disabled={channelSelected}
          onChange={(tickerValue) => setDraft((d) => ({ ...d, ticker: tickerValue.toUpperCase() }))}
        />
        {channelSelected ? (
          <div className="field">
            <label>{copy.metadata}</label>
            {imagePreview ? <img className="launch-image-preview" src={imagePreview} alt={copy.metadata} />
              : <div className="launch-image-empty">{copy.metadataRequired}</div>}
            <input type="file" accept="image/png,image/jpeg,image/webp" aria-label={copy.replaceImage} onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
                setLocalError(copy.metadataHint);
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                const encoded = String(reader.result ?? '').split(',')[1] ?? '';
                setDraft((d) => ({ ...d, imageBase64: encoded, imageMime: file.type }));
                setLocalError(null);
              };
              reader.readAsDataURL(file);
            }} />
            {draft.imageBase64 && channelImageUrl ? (
              <button type="button" className="ghost" onClick={() => setDraft((d) => ({ ...d, imageBase64: '', imageMime: '' }))}>
                {copy.useChannelImage}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <p className="hint">{copy.nameHint}</p>
      <p className="hint">{copy.tickerHint}</p>
      <p className="hint">{channelSelected ? imagePreview ? copy.metadataHint : copy.metadataRequired : copy.chooseChannelHint}</p>
      <div className="section-label">{copy.seed}</div>
      <div className="group">
        <Field
          label="TON"
          value={draft.seedTon}
          onChange={(seedTon) => setDraft((d) => ({ ...d, seedTon }))}
          inputMode="decimal"
        />
      </div>
      <p className="hint">{copy.seedHint}</p>
      <ChipRow
        values={config.graduationThreshold <= toNano('2') ? ['0', '0.1', '0.5', '1.01'] : ['0', '10', '50', '100', '1000']}
        current={draft.seedTon}
        onPick={(seedTon) => setDraft((d) => ({ ...d, seedTon }))}
      />

      <div className="section-label">{copy.devBuy}</div>
      <div className="group">
        <Field
          label="TON"
          value={draft.devBuyTon}
          onChange={(devBuyTon) => setDraft((d) => ({ ...d, devBuyTon }))}
          inputMode="decimal"
        />
      </div>
      <p className="hint">{copy.devBuyHint}</p>
      <ChipRow
        values={config.graduationThreshold <= toNano('2') ? ['0', '0.05', '0.1', '0.2'] : ['0', '1', '2', '5']}
        current={draft.devBuyTon}
        onPick={(devBuyTon) => setDraft((d) => ({ ...d, devBuyTon }))}
      />

      <div className="section-label">{copy.review}</div>
      {preview.ok ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <PhaseBadge phase={preview.value.state.phase} />
          </div>
          <SeedDevBuyLines
            copy={copy}
            ticker={ticker}
            lines={launchPassportLines(
              preview.value.seedTon,
              preview.value.devBuyTon,
              preview.value.seed.jettons,
              preview.value.devBuy.jettons,
            )}
          />
          {preview.value.publicCurveClosed ? (
            <div className="banner warn" style={{ marginTop: 12 }}>
              {copy.publicCurveClosed}
            </div>
          ) : null}
          <div className="group" style={{ marginTop: 12 }}>
            <div className="cell">
              <span className="label">{copy.launchFee}</span>
              <span className="value">{formatTon(launchFee)}</span>
            </div>
            <div className="cell">
              <span className="label">{copy.devBuyLine}</span>
              <span className="value">{formatJettons(preview.value.devBuy.jettons, ticker)}</span>
            </div>
            {preview.value.refund > 0n ? (
              <div className="cell">
                <span className="label">{copy.refund}</span>
                <span className="value">{formatTon(preview.value.refund)}</span>
              </div>
            ) : null}
            <div className="cell">
              <span className="label">{copy.totalToSign}</span>
              <span className="value">{formatTon(total)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="banner warn">{preview.error.message}</div>
      )}

      {shownError ? <div className="banner warn">{shownError}</div> : null}

      <div className="btn-row">
        <button type="button" className="btn" disabled={busy || paused || !ready || !preview.ok || !HAS_FACTORY} onClick={submit}>
          {busy ? busyLabel ?? copy.sending : channelSelected ? copy.send : copy.chooseChannel}
        </button>
      </div>
      <RiskNote copy={copy} />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: 'decimal' | 'text';
  disabled?: boolean;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <input
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        disabled={disabled}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ChipRow({
  values,
  current,
  onPick,
}: {
  values: string[];
  current: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="chips">
      {values.map((value) => (
        <button
          key={value}
          type="button"
          className={`chip${current === value ? ' on' : ''}`}
          onClick={() => onPick(value)}
        >
          {value} TON
        </button>
      ))}
    </div>
  );
}

function safeParse(value: string): bigint {
  try {
    if (!value.trim()) {
      return 0n;
    }
    return parseTon(value);
  } catch {
    return toNano('0');
  }
}
